<div class="rp-page">

  <!-- Header strip -->
  <div class="rp-header">
    <div class="rp-header-left">
      <h2><i class="bi bi-arrow-counterclockwise"></i> Return Policy</h2>
      <p class="rp-subtitle">
        Caps and rules the cashier flow enforces. Picks store-specific over the global default.
      </p>
    </div>

    <div class="rp-header-right">
      <label class="rp-scope-label">Scope</label>
      <ng-select
        class="rp-scope-select"
        [items]="stores()"
        bindLabel="name"
        bindValue="id"
        placeholder="Global default"
        [ngModel]="selectedStoreId()"
        (ngModelChange)="onStoreChange($event)"
        [clearable]="true"
      ></ng-select>
    </div>
  </div>

  <!-- Loading -->
  @if (isLoading()) {
    <div class="rp-loading">Loading policy…</div>
  } @else {
    <form #form="ngForm" class="rp-form" (ngSubmit)="save(form)">

      <!-- F6: master switch — most prominent control on the page -->
      <section class="rp-section rp-master">
        <div class="rp-master-row">
          <div class="rp-master-text">
            <h3>Returns at this {{ selectedStoreId() ? 'store' : 'tenant (default)' }}</h3>
            <p>
              When OFF, cashiers at this scope do not see the Returns button.
              Flip ON when the store is trained and ready to accept returns.
              Terminals pick up changes within 5 minutes of saving.
            </p>
          </div>
          <label class="rp-switch" [class.on]="policy().isReturnsEnabled">
            <input
              type="checkbox"
              [checked]="policy().isReturnsEnabled"
              (change)="patch('isReturnsEnabled', !policy().isReturnsEnabled)"
              name="isReturnsEnabled"
              aria-label="Enable returns at this store"
            />
            <span class="rp-switch-track">
              <span class="rp-switch-thumb"></span>
            </span>
            <span class="rp-switch-label">
              {{ policy().isReturnsEnabled ? 'Enabled' : 'Disabled' }}
            </span>
          </label>
        </div>
      </section>

      <!-- Section 1: Time windows -->
      <section class="rp-section">
        <div class="rp-section-head">
          <h3>Return windows</h3>
          <p>Days after the sale a return may be accepted. Zero = no return for that category.</p>
        </div>
        <div class="rp-grid rp-grid-3">
          <label class="rp-field">
            <span>Standard items</span>
            <div class="rp-input-suffix">
              <input
                type="number" min="0" max="365" required
                [ngModel]="policy().windowDaysStandard"
                (ngModelChange)="patch('windowDaysStandard', $event)"
                name="windowDaysStandard"
              />
              <span class="rp-suffix">days</span>
            </div>
          </label>

          <label class="rp-field">
            <span>Perishable</span>
            <div class="rp-input-suffix">
              <input
                type="number" min="0" max="30" required
                [ngModel]="policy().windowDaysPerishable"
                (ngModelChange)="patch('windowDaysPerishable', $event)"
                name="windowDaysPerishable"
              />
              <span class="rp-suffix">days</span>
            </div>
            <small class="rp-hint">0 = perishable items not returnable</small>
          </label>

          <label class="rp-field">
            <span>Sale items</span>
            <div class="rp-input-suffix">
              <input
                type="number" min="0" max="90" required
                [ngModel]="policy().windowDaysSaleItem"
                (ngModelChange)="patch('windowDaysSaleItem', $event)"
                name="windowDaysSaleItem"
              />
              <span class="rp-suffix">days</span>
            </div>
            <small class="rp-hint">0 = sale items final</small>
          </label>
        </div>
      </section>

      <!-- Section 2: Refund caps -->
      <section class="rp-section">
        <div class="rp-section-head">
          <h3>Refund authority caps</h3>
          <p>Per-tier rupee ceilings. Above the cap, the next tier's approval is required.</p>
        </div>
        <div class="rp-grid rp-grid-3">
          <label class="rp-field">
            <span>Cashier</span>
            <div class="rp-input-suffix">
              <span class="rp-prefix">₹</span>
              <input
                type="number" min="0" step="100" required
                [ngModel]="policy().cashierRefundCap"
                (ngModelChange)="patch('cashierRefundCap', $event)"
                name="cashierRefundCap"
              />
            </div>
          </label>

          <label class="rp-field">
            <span>Shift supervisor</span>
            <div class="rp-input-suffix">
              <span class="rp-prefix">₹</span>
              <input
                type="number" min="0" step="500" required
                [ngModel]="policy().supervisorRefundCap"
                (ngModelChange)="patch('supervisorRefundCap', $event)"
                name="supervisorRefundCap"
              />
            </div>
          </label>

          <label class="rp-field">
            <span>Store manager</span>
            <div class="rp-input-suffix">
              <span class="rp-prefix">₹</span>
              <input
                type="number" min="0" step="1000" required
                [ngModel]="policy().managerRefundCap"
                (ngModelChange)="patch('managerRefundCap', $event)"
                name="managerRefundCap"
              />
            </div>
          </label>
        </div>
        @if (ladderError(); as err) {
          <div class="rp-error">
            <i class="bi bi-exclamation-triangle"></i> {{ err }}
          </div>
        }
      </section>

      <!-- Section 3: Behavioral knobs -->
      <section class="rp-section">
        <div class="rp-section-head">
          <h3>Behaviour</h3>
          <p>Edge-case rules the engine consults during eligibility checks.</p>
        </div>
        <div class="rp-toggle-grid">
          <label class="rp-toggle">
            <input
              type="checkbox"
              [ngModel]="policy().allowOfferLockedReturn"
              (ngModelChange)="patch('allowOfferLockedReturn', $event)"
              name="allowOfferLockedReturn"
            />
            <span class="rp-toggle-text">
              <strong>Allow returns of offer-locked items</strong>
              <small>BuyXGetY trigger lines, BillRange-disqualifying items.</small>
            </span>
          </label>

          <label class="rp-toggle">
            <input
              type="checkbox"
              [ngModel]="policy().requireOfferOverridePin"
              (ngModelChange)="patch('requireOfferOverridePin', $event)"
              name="requireOfferOverridePin"
            />
            <span class="rp-toggle-text">
              <strong>Manager PIN to override offer locks</strong>
              <small>Forces an approver on every offer-bound return.</small>
            </span>
          </label>

          <label class="rp-toggle">
            <input
              type="checkbox"
              [ngModel]="policy().allowCrossDayCashRefund"
              (ngModelChange)="patch('allowCrossDayCashRefund', $event)"
              name="allowCrossDayCashRefund"
            />
            <span class="rp-toggle-text">
              <strong>Cross-day cash refunds</strong>
              <small>Off → store credit only for bills older than today.</small>
            </span>
          </label>

          <label class="rp-toggle">
            <input
              type="checkbox"
              [ngModel]="policy().allowCrossStoreReturn"
              (ngModelChange)="patch('allowCrossStoreReturn', $event)"
              name="allowCrossStoreReturn"
            />
            <span class="rp-toggle-text">
              <strong>Cross-store returns</strong>
              <small>Permit returning a bill from another store of the chain.</small>
            </span>
          </label>
        </div>
      </section>

      <!-- Section 4: Blocked categories / departments -->
      <section class="rp-section">
        <div class="rp-section-head">
          <h3>Always-blocked items</h3>
          <p>Items in these categories or departments are never returnable, regardless of reason.</p>
        </div>
        <div class="rp-grid rp-grid-2">
          <label class="rp-field">
            <span>Blocked categories</span>
            <ng-select
              [items]="categories()"
              bindLabel="name"
              bindValue="id"
              [multiple]="true"
              placeholder="Select categories"
              [ngModel]="policy().blockedCategoryIds"
              (ngModelChange)="patch('blockedCategoryIds', $event || [])"
              name="blockedCategoryIds"
            ></ng-select>
          </label>

          <label class="rp-field">
            <span>Blocked departments</span>
            <ng-select
              [items]="departments()"
              bindLabel="name"
              bindValue="id"
              [multiple]="true"
              placeholder="Select departments"
              [ngModel]="policy().blockedDepartmentIds"
              (ngModelChange)="patch('blockedDepartmentIds', $event || [])"
              name="blockedDepartmentIds"
            ></ng-select>
          </label>
        </div>
        <small class="rp-hint">Brand blocklist ships in a follow-up alongside the brands endpoint.</small>
      </section>

      <!-- Footer actions -->
      <div class="rp-footer">
        <div class="rp-footer-meta">
          @if (isDirty()) {
            <span class="rp-meta-dirty"><i class="bi bi-pencil"></i> Unsaved changes</span>
          } @else {
            <span class="rp-meta-clean"><i class="bi bi-check-circle"></i> Up to date</span>
          }
        </div>
        <div class="rp-footer-actions">
          <button type="button" class="rp-btn ghost" (click)="loadHistory()">
            <i class="bi bi-clock-history"></i> History
          </button>
          <button
            type="button"
            class="rp-btn ghost"
            (click)="resetToBaseline()"
            [disabled]="!isDirty()"
          >
            Reset
          </button>
          <button
            type="submit"
            class="rp-btn primary"
            [disabled]="!isDirty() || isSaving() || !!ladderError()"
          >
            @if (isSaving()) {
              <i class="bi bi-arrow-repeat rp-spin"></i> Saving…
            } @else {
              <i class="bi bi-check2-circle"></i> Save policy
            }
          </button>
        </div>
      </div>
    </form>

    <!-- History drawer -->
    @if (showHistory()) {
      <div class="rp-history-overlay" (click)="hideHistory()">
        <div class="rp-history-panel" (click)="$event.stopPropagation()">
          <div class="rp-history-head">
            <h3>Policy versions</h3>
            <button class="rp-btn ghost" (click)="hideHistory()" type="button">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          @if (history().length === 0) {
            <div class="rp-history-empty">No prior versions for this scope yet.</div>
          } @else {
            <table class="rp-history-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Status</th>
                  <th>Cashier cap</th>
                  <th>Supervisor cap</th>
                  <th>Manager cap</th>
                  <th>Std window</th>
                </tr>
              </thead>
              <tbody>
                @for (h of history(); track h.id) {
                  <tr [class.active]="h.isActive">
                    <td>{{ h.updatedAt | date:'medium' }}</td>
                    <td>
                      @if (h.isActive) {
                        <span class="rp-pill active">Active</span>
                      } @else {
                        <span class="rp-pill archived">Archived</span>
                      }
                    </td>
                    <td>₹{{ h.cashierRefundCap | number:'1.0-0' }}</td>
                    <td>₹{{ h.supervisorRefundCap | number:'1.0-0' }}</td>
                    <td>₹{{ h.managerRefundCap | number:'1.0-0' }}</td>
                    <td>{{ h.windowDaysStandard }}d</td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>
      </div>
    }
  }
</div>
